import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import type { BattleState } from '@somnhold/sim';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** A gutter's gate: a channel, the gate standing in it, a wheel over it. */
const GATE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="5" y="6" width="6" height="5.5" rx="0.6" fill="currentColor"/><circle cx="8" cy="3" r="1.8" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
/** A gargoyle's open jaws, water pouring from them. */
const GARGOYLE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 3.5l3.5 2 3-2.5 3 2 2.5-1.5-1 4.5-3 1.5-5 .5z" fill="currentColor"/><path d="M6.5 9.5c-.5 2-1 3.5-2.5 5M8.5 9.6c0 2-.3 3.6-1 5M10.5 9.2c.6 1.8 1.2 3.4 2.5 5" stroke="currentColor" stroke-width="1.2" fill="none" stroke-linecap="round" opacity="0.8"/></svg>';
/** The cistern: a round basin, water in it. */
const CISTERN = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="5" rx="6" ry="2.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2 5v6c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2V5" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2.6 8.5c1.6.8 3.4 1.2 5.4 1.2s3.8-.4 5.4-1.2V11c0 1-2.4 1.8-5.4 1.8S2.6 12 2.6 11z" fill="currentColor"/></svg>';
/** Rain: slanting streaks under a cloud. */
const RAIN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 7.5a3 3 0 0 1 .4-6 3.6 3.6 0 0 1 6.9 1A2.5 2.5 0 0 1 12 7.5z" fill="currentColor"/><path d="M5 9.5l-1.3 3M8.3 9.5L7 12.5M11.6 9.5l-1.3 3M6.7 12.8l-.8 2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** The aura: a ring round a shield. */
const AURA = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" stroke-width="1.1" stroke-dasharray="2 1.6"/><path d="M8 4.2l3 1.1v2.4c0 2-1.3 3.4-3 4.1-1.7-.7-3-2.1-3-4.1V5.3z" fill="currentColor"/></svg>';
/** A mammoth's head, tusks down. */
const MAMMOTH = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 9c0-3.6 2.3-6 5-6s5 2.4 5 6c0 1.4-.6 2.4-1.6 2.6L10 9.5 8.8 13H7.2L6 9.5l-1.4 2.1C3.6 11.4 3 10.4 3 9z" fill="currentColor"/><path d="M5 10.5c-.4 1.7-1.4 2.8-2.6 3M11 10.5c.4 1.7 1.4 2.8 2.6 3" stroke="currentColor" stroke-width="1.2" fill="none" stroke-linecap="round"/></svg>';

/** Where the bridge hangs its marks (render's ArchLayer anchors, world points). */
export interface ArchAnchors {
  levers: THREE.Vector3[];
  cistern: THREE.Vector3 | null;
  ends: THREE.Vector3[];
}

/**
 * The bridge's altar on screen (docs/sendas/paladin.md, chapter 3), inside a path's own plate (game/senda-hud.ts) and
 * over the board, short enough that a phone's plate stays under about a quarter of the screen:
 *
 * - every act: what the paladin's aura covers now (both ends, one, none), and a band seen below, red, by end.
 * - the gutters and the night: the cistern's eight marks (the night: the half that keeps the altar burning marked, and
 *   «¡Se apaga!» counting while below it); the rain's half and when it moves («¡Se corre la lluvia!» as it does). Over
 *   the board, a mark on each lever, tappable (the hero goes and works it), its ring filling as it does: the gutters'
 *   gates read open, shut or blocked; the gargoyles read their cost and go dim without four marks.
 * - the night: the next mammoth (in how long, climbing), and «¡Baja la cabeza! Gárgola» red while its head is down.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md).
 */
export class ArchHud {
  readonly panel: HTMLElement;
  private kind: 'ends' | 'gutters' | 'vigil';
  private v = new THREE.Vector3();
  private cover = h('span.al-wave');
  private coverRow = h('div.bh-row.ar-cover');
  private band = h('span.al-wave');
  private bandRow = h('div.bh-row.al-waverow.warn');
  private pips: HTMLElement[] = [];
  private poolRow = h('div.bh-row.ar-pool');
  private poolText = h('span.al-wave');
  private rain = h('span.al-wave');
  private rainRow = h('div.bh-row.al-waverow');
  private beast = h('span.al-wave');
  private beastRow = h('div.bh-row.al-waverow');
  private marks: { el: HTMLButtonElement; state: HTMLElement }[] = [];
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.arch;
    this.kind = r?.kind ?? 'ends';
    this.coverRow.append(h('span.bh-ico', { html: AURA }), h('span.bh-label', {}, t('senda.arch.cover')), this.cover);
    this.bandRow.append(h('span.bh-ico', { html: ICONS.skull }), this.band);
    this.bandRow.hidden = true;
    // (The aura's row is the two ends' lesson; later the plate keeps to the water, so a phone's stays short.)
    const rows: HTMLElement[] = this.kind === 'ends' ? [this.coverRow] : [];
    if (r?.cistern) {
      this.pips = Array.from({ length: r.cistern.cap }, (_, i) => h(i === r.cistern!.lit - 1 && this.kind === 'vigil' ? 'i.half' : 'i'));
      this.poolRow.append(h('span.bh-ico', { html: CISTERN }), h('span.bh-label', {}, t('senda.arch.cistern')), h('span.al-pips.ar-water', {}, ...this.pips), this.poolText);
      this.rainRow.append(h('span.bh-ico', { html: RAIN }), h('span.bh-label', {}, t('senda.arch.rain')), this.rain);
      rows.unshift(this.poolRow, this.rainRow);
      const names = ['senda.arch.gutterLow', 'senda.arch.gutterHigh', 'senda.arch.gargoyleLow', 'senda.arch.gargoyleHigh'];
      this.marks = r.levers.map((_, i) => {
        const state = h('small.al-state');
        const el = h(
          `button.al-stone.ar-lever${i < 2 ? '.gate' : '.gargoyle'}`,
          { type: 'button', 'data-test': `arch-lever-${i}`, 'aria-label': `${t(names[i] as string)}: ${t(i < 2 ? 'senda.arch.gateHint' : 'senda.arch.gargoyleHint')}`, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(i)) },
          h('span.al-ring'),
          h('span.al-glyph', { html: i < 2 ? GATE : GARGOYLE }),
          state,
        ) as HTMLButtonElement;
        this.board.append(el);
        return { el, state };
      });
    }
    if (r?.mammoth) {
      this.beastRow.append(h('span.bh-ico', { html: MAMMOTH }), h('span.bh-label', {}, t('senda.arch.mammoth')), this.beast);
      rows.push(this.beastRow);
    }
    rows.push(this.bandRow);
    this.panel = h('div.bh.al-canal.ar-panel', {}, ...rows);
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.arch;
    const w = s.senda?.arch;
    if (!r || !w) return;
    // What the aura covers: the keepers of the lower end (Marit) and of the upper (Varo and his knights).
    let low = false;
    let high = false;
    (s.senda?.npcs ?? []).forEach((n, i) => {
      if (!w.covered[i]) return;
      if (n.id === 'marit') low = true;
      else if (r.keeps.some((k) => k.id === n.id)) high = true;
    });
    const cover = low && high ? t('senda.arch.coverBoth') : low ? t('senda.arch.coverLow') : high ? t('senda.arch.coverHigh') : t('senda.arch.coverNone');
    if (this.cover.textContent !== cover) this.cover.textContent = cover;
    this.coverRow.classList.toggle('both', low && high);
    // A band seen below, by end, until it sets foot on its stairs.
    const B = r.bands;
    const seen = w.warned && w.band < B.at.length;
    const side = B.side[w.band] ?? 0;
    const band = seen ? t(side === 2 ? 'senda.arch.bandBoth' : side === 1 ? 'senda.arch.bandHigh' : 'senda.arch.bandLow') : '';
    if (this.band.textContent !== band) this.band.textContent = band;
    this.bandRow.hidden = !seen;
    const C = r.cistern;
    if (C) {
      const key = `${w.level}|${w.open.join('')}|${w.blocked.join('')}|${w.rain}`;
      if (key !== this.key) {
        this.key = key;
        this.pips.forEach((p, i) => p.classList.toggle('on', i < w.level));
        this.marks.forEach((m, i) => {
          if (i < 2) {
            const open = w.open[i] === 1;
            const blocked = w.blocked[i] === 1 && w.rain === i;
            m.el.classList.toggle('shut', !open);
            m.el.classList.toggle('runs', open && w.rain === i && !blocked);
            m.el.classList.toggle('blocked', blocked);
            m.state.textContent = blocked ? t('senda.arch.blocked') : open ? t('senda.arch.open') : t('senda.arch.shut');
          } else {
            const dry = w.level < r.sweep.drop;
            m.el.classList.toggle('dry', dry);
            m.state.textContent = `−${r.sweep.drop}`;
            m.el.title = dry ? t('senda.arch.noWater') : '';
          }
        });
      }
      // The night: the altar burns on half; below it, how long is left to save it.
      const dark = r.kind === 'vigil' && w.dark >= 0 && !w.out;
      const pool = r.kind === 'vigil' ? (dark ? t('senda.arch.dark', { s: Math.max(0, Math.ceil((w.dark + C.dark - s.tick) / TICK_HZ)) }) : w.level >= C.lit ? t('senda.arch.lit') : '') : '';
      if (this.poolText.textContent !== pool) this.poolText.textContent = pool;
      this.poolRow.classList.toggle('dark', dark);
      // The rain: where it pours now, and when it moves.
      const moving = s.waveT >= w.rainAt - r.rain.warn;
      const rain = moving ? t('senda.arch.rainMoving') : `${t(w.rain === 0 ? 'senda.arch.rainLow' : 'senda.arch.rainHigh')} · ${t('senda.arch.rainIn', { s: Math.max(1, Math.ceil((w.rainAt - s.waveT) / TICK_HZ)) })}`;
      if (this.rain.textContent !== rain) this.rain.textContent = rain;
      this.rainRow.classList.toggle('warn', moving);
      this.marks.forEach((m, i) => {
        const sent = w.job === i;
        m.el.classList.toggle('sent', sent);
        m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.turn)).toFixed(2) : '0');
        m.el.disabled = s.result !== 'running';
      });
    }
    const M = r.mammoth;
    if (M) {
      let text = '';
      let warn = false;
      for (let k = 0; k < 2; k++) {
        const id = w.mammoth[k] as number;
        if (id > 0) {
          warn = w.mode[k] === 1;
          text = warn ? t('senda.arch.lowering') : `${t(k === 0 ? 'senda.arch.low' : 'senda.arch.high')} · ${t('senda.arch.mammothUp')}`;
          break;
        }
        if (id === 0) {
          text = `${t(k === 0 ? 'senda.arch.low' : 'senda.arch.high')} · ${t('senda.arch.mammothIn', { s: Math.max(1, Math.ceil(((M.at[k] as number) - s.waveT) / TICK_HZ)) })}`;
          break;
        }
      }
      if (this.beast.textContent !== text) this.beast.textContent = text;
      this.beastRow.classList.toggle('warn', warn);
      this.beastRow.hidden = !text;
    }
  }

  place(camera: THREE.Camera, w: number, ht: number, a: ArchAnchors | null): void {
    if (!a || !this.marks.length) return;
    this.marks.forEach((m, i) => {
      const p = a.levers[i];
      if (!p) return setStyle(m.el, 'display', 'none');
      this.v.copy(p).project(camera);
      if (this.v.z > 1) return setStyle(m.el, 'display', 'none');
      setStyle(m.el, 'display', '');
      pinMark(m.el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
    });
  }
}
